type InitTableRowReorderParams = {
  delayMs?: number;
  onReorder?: () => void | Promise<void>;
  onIndent?: () => void | Promise<void>;
  onUnindent?: () => void | Promise<void>;
  indentThreshold?: number;
};

type DragState = {
  dragSource: HTMLTableRowElement | null;
  dragTarget: HTMLTableRowElement | null;
  dragTimeout: ReturnType<typeof setTimeout> | undefined;
  dragStartX: number;
  dragStartY: number;
};

const dragStateByContainer = new WeakMap<Element, DragState>();

export const initTableRowReorder = (tr: HTMLTableRowElement, params: InitTableRowReorderParams = {}) => {
  const { delayMs = 100, onReorder, onIndent, onUnindent, indentThreshold = 40 } = params;
  const container = tr.parentElement;
  const handle = tr.querySelector('.reorder');

  if (!container || !handle) {
    return;
  }

  tr.draggable = true;

  const dragState = dragStateByContainer.get(container) ?? {
    dragSource: null,
    dragTarget: null,
    dragTimeout: undefined,
    dragStartX: 0,
    dragStartY: 0,
  };

  dragStateByContainer.set(container, dragState);

  let dragFromHandle = false;

  handle.addEventListener('mousedown', () => {
    dragFromHandle = true;
  });

  handle.addEventListener('mouseup', () => {
    dragFromHandle = false;
  });

  const resetDragState = () => {
    if (dragState.dragSource) {
      dragState.dragSource.style.opacity = '1';
    }

    dragState.dragSource = null;
    dragState.dragTarget = null;

    if (dragState.dragTimeout) {
      clearTimeout(dragState.dragTimeout);
      dragState.dragTimeout = undefined;
    }
  };

  tr.addEventListener('dragstart', (e) => {
    if (!dragFromHandle) {
      e.preventDefault();
      return;
    }

    dragFromHandle = false;

    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'move';
      e.dataTransfer.effectAllowed = 'move';
    }

    dragState.dragSource = tr;
    dragState.dragStartX = e.clientX;
    dragState.dragStartY = e.clientY;
  });

  tr.addEventListener('dragover', (e) => {
    e.preventDefault();

    if (dragState.dragSource && !dragState.dragTimeout) {
      const target = e.currentTarget as HTMLTableRowElement;

      if (target !== dragState.dragSource) {
        dragState.dragTarget = target;
        dragState.dragSource.style.opacity = '0';

        dragState.dragTimeout = setTimeout(() => {
          if (dragState.dragSource && dragState.dragTarget) {
            const rows = [...dragState.dragTarget.parentElement!.children];

            if (rows.indexOf(dragState.dragTarget) > rows.indexOf(dragState.dragSource)) {
              dragState.dragTarget.after(dragState.dragSource);
            } else {
              dragState.dragTarget.before(dragState.dragSource);
            }
          }

          dragState.dragTimeout = undefined;
        }, delayMs);
      }
    }
  });

  tr.addEventListener('drop', (e) => {
    e.preventDefault();
  });

  tr.addEventListener('dragend', async (e) => {
    dragFromHandle = false;

    const dragSource = dragState.dragSource;

    if (!dragSource || dragSource !== e.currentTarget) return;

    const deltaX = e.clientX - dragState.dragStartX;
    const deltaY = e.clientY - dragState.dragStartY;
    const isHorizontal = Math.abs(deltaX) >= indentThreshold && Math.abs(deltaX) > Math.abs(deltaY);

    dragSource.style.opacity = '1';

    if (isHorizontal && (onIndent || onUnindent)) {
      if (deltaX > 0) {
        await onIndent?.();
      } else {
        await onUnindent?.();
      }
    } else if (dragState.dragTarget) {
      await onReorder?.();
    }

    resetDragState();
  });
};
